import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Phone, CheckCircle, ArrowRight, ArrowLeftRight } from "lucide-react";
import { services, getBusinessConfig } from "@/lib/site-config";
import { faqSchema, FAQAccordion } from "@/components/FAQAccordion";
import { HelpfulGuidesGrid } from "@/components/HelpfulGuidesGrid";
import { ServiceHero } from "@/components/ServiceHero";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { ProcessSteps } from "@/components/ProcessSteps";
import { CTABlock } from "@/components/CTABlock";
import { WhyChooseWoodseer } from "@/components/WhyChooseWoodseer";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { seoHead, serviceSchema, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { listPublicGalleryPhotos } from "@/lib/gallery.functions";
import { BodyworkBeforeAfter } from "@/components/BodyworkBeforeAfter";

const svc = services[4]; // bodywork

const extendedFaqs = [
  ...svc.faqs,
  { q: "Can I send you photos for a quick estimate?", a: "Yes — send photos via WhatsApp and we'll give you a rough idea of cost. For an accurate quote, bring the car in so we can assess the damage properly." },
  { q: "Will the repair be noticeable?", a: "Our goal is always a seamless finish. With professional colour matching and proper preparation, the repaired area should be indistinguishable from the rest of the vehicle." },
  { q: "Do you handle larger accident damage?", a: "We handle a wide range of bodywork — from minor dents and scratches to larger collision repairs. For structural damage, we'll assess and advise honestly on the best course of action." },
  { q: "How much does bodywork cost?", a: "Smart repairs (small dents, stone chips, light scuffs) from £80. Bumper repairs from £180. Single-panel respray from £350. Multi-panel and accident damage quoted after inspection — we never start work without a written figure." },
  { q: "How long does a bodywork repair take?", a: "Smart repairs are usually same day. A single panel respray takes 2–3 days to allow paint to cure properly. Larger collision repairs vary — we give a realistic completion date when you book and keep you updated." },
  { q: "What's included in the repair?", a: "Damage assessment, panel prep, dent removal or filling, colour-matched paint (mixed in-house), clear-coat, polishing and blending into surrounding panels. All repairs come with a written guarantee on workmanship and paint finish." },
];


const beforeAfterExamples = [
  { type: "Bumper Scuff", before: "Deep scuff and crack on front bumper from a low-speed collision", after: "Bumper repaired, filled, and colour-matched — invisible repair" },
  { type: "Car Park Dent", before: "Door panel dent from another car's door in a car park", after: "Dent removed with paintless repair — no respray needed" },
  { type: "Scratch Repair", before: "Long key scratch across rear quarter panel", after: "Panel sanded, primed, resprayed and blended with surrounding panels" },
  { type: "Paint Fade", before: "Faded, peeling clear coat on bonnet from sun damage", after: "Bonnet resprayed and lacquered — restored to showroom finish" },
];

export const Route = createFileRoute("/bodywork")({
  loader: async () => {
    const [cfg, gallery] = await Promise.all([
      getPublicConfig().catch(() => null),
      listPublicGalleryPhotos({ data: { limit: 24 } }).catch(() => ({ photos: [] })),
    ]);
    const bodyworkPhotos = (gallery.photos || [])
      .filter((p: { category?: string }) => (p.category || "").toLowerCase() === "bodywork")
      .slice(0, 3)
      .map((p: { public_url: string; alt_text: string }) => ({ url: p.public_url, alt: p.alt_text }));
    return { compliance: cfg?.compliance || null, bodyworkPhotos };
  },
  head: ({ loaderData }) => seoHead({
    path: "/bodywork",
    title: "Bodywork & Paint Repair East London E14 | Woodseer Autos",
    description: `Dent removal, scratch repair, paint matching and bumper repair in East London. WhatsApp photos for a free estimate. Call Woodseer Autos: ${NAP.phone}.`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Bodywork", path: "/bodywork" },
    ],
    jsonLd: [
      localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
      faqSchema(extendedFaqs),
      serviceSchema({
        name: "Bodywork & Paint Repair — Woodseer Autos",
        serviceType: "Bodywork & Paint Repair",
        description: "Dent removal, scratch repair, paint matching, bumper replacement and collision damage in East London.",
        offers: { price: "80.00", priceCurrency: "GBP", description: "Smart repairs from £80; full panel respray from £350 — exact quote after inspection." },
      }),
    ],
  }),
  component: BodyworkPage,
});

function BodyworkPage() {
  const biz = getBusinessConfig();
  const { bodyworkPhotos } = Route.useLoaderData();

  return (
    <>
      <ServiceHero
        headline={svc.headline}
        intro={svc.intro}
        image="/images/hero-bodywork.jpg"
        imageAlt="Bodywork repair at Woodseer Autos, East London"
      />

      <ReassuranceBanner />

      {/* What's included */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">What We Can Fix</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
              {svc.included.map((item) => (
                <StaggerItem key={item}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {item}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Before / After */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">What We Fix — Before & After</h2>
            <p className="mt-3 text-center text-muted-foreground">Here's what typical bodywork repairs look like. Every job is finished to a high standard with professional colour matching.</p>
            <StaggerContainer className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {beforeAfterExamples.map((ex) => (
                <StaggerItem key={ex.type}>
                  <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="p-4 border-b border-border">
                      <h3 className="font-semibold text-foreground flex items-center gap-2">
                        <ArrowLeftRight className="h-4 w-4 text-primary" />
                        {ex.type}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 divide-x divide-border">
                      <div className="p-4">
                        <p className="text-xs font-semibold text-destructive uppercase tracking-wider mb-2">Before</p>
                        <p className="text-sm text-muted-foreground">{ex.before}</p>
                      </div>
                      <div className="p-4">
                        <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">After</p>
                        <p className="text-sm text-muted-foreground">{ex.after}</p>
                      </div>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Common damage */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Common Damage We See</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {svc.commonIssues.map((issue) => (
                <StaggerItem key={issue}>
                  <div className="flex items-start gap-3 rounded-xl border border-border p-4 text-sm text-muted-foreground bg-background/50">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                    {issue}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Signs */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Signs You Need Bodywork</h2>
            <ul className="mt-8 space-y-3 max-w-xl mx-auto">
              {svc.signs.map((sign) => (
                <li key={sign} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <span className="text-primary font-bold">→</span>
                  {sign}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </AnimatedSection>

      {/* Finish quality reassurance */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="text-3xl font-bold text-foreground">Quality You Can Trust</h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              We take pride in the finish. Every repair goes through proper preparation — sanding, priming, painting and clear coating — with professional colour matching to ensure a seamless result. We don't cut corners and we communicate clearly throughout so you know exactly what's happening with your car.
            </p>
          </div>
        </section>
      </AnimatedSection>

      {/* Why choose us */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Why Choose Woodseer Autos for Bodywork</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {svc.whyUs.map((reason) => (
                <StaggerItem key={reason}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {reason}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection>
        <ProcessSteps steps={svc.process} title="How Bodywork Repair Works" />
      </AnimatedSection>

      <AnimatedSection>
      <HelpfulGuidesGrid slugs={["selling-car-repair-checklist","tyre-check-before-mot","mot-warning-lights-before-test"]} title="Helpful guides for this service" compact />

        <FAQAccordion faqs={extendedFaqs} />
      </AnimatedSection>

      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-2xl font-bold text-foreground text-center">Other Services</h2>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {services.filter((s) => s.slug !== "bodywork").map((s) => (
                <Link key={s.slug} to={`/${s.slug}` as any} className="flex flex-col items-center rounded-xl border border-border bg-background/50 p-4 hover:border-primary/40 transition-all text-center group">
                  <span className="text-sm font-semibold text-foreground">{s.name}</span>
                  <ArrowRight className="mt-1 h-3.5 w-3.5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Local areas */}
      <AnimatedSection>
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-5xl px-4 text-center">
            <p className="text-sm text-muted-foreground">Bodywork repairs for drivers in{" "}
              <Link to="/areas/$area" params={{ area: "poplar" }} className="text-primary hover:text-primary/80">Poplar</Link>,{" "}
              <Link to="/areas/$area" params={{ area: "canary-wharf" }} className="text-primary hover:text-primary/80">Canary Wharf</Link>,{" "}
              <Link to="/areas/$area" params={{ area: "isle-of-dogs" }} className="text-primary hover:text-primary/80">Isle of Dogs</Link> and the wider{" "}
              <Link to="/areas/$area" params={{ area: "tower-hamlets" }} className="text-primary hover:text-primary/80">Tower Hamlets</Link> area.
            </p>
          </div>
        </section>
      </AnimatedSection>

      {bodyworkPhotos && bodyworkPhotos.length > 0 && (
        <BodyworkBeforeAfter photos={bodyworkPhotos} />
      )}

      <WhyChooseWoodseer page="bodywork" />
      <CTABlock headline="Need Bodywork Done?" subtext="Send us photos via WhatsApp for a quick estimate, or call us to book an assessment. Fair pricing, quality finish." />
    </>
  );
}
